import { Combobox } from "@base-ui/react/combobox";
import { Field } from "@base-ui/react/field";
import { Select as BaseSelect } from "@base-ui/react/select";
import clsx from "clsx";
import type React from "react";
import { forwardRef } from "react";
import { vars } from "../../theme/theme.css";
import { FieldMessage } from "../FieldInfo/FieldMessage";
import { IconChevronDown, IconClose } from "../icons";
import { Spinner } from "../Spinner";
import {
  clearButtonClass,
  comboboxInputClass,
  comboboxInputSizeVariants,
  itemIndicatorClass,
  multiValueBadgeClass,
  noOptionsMessageClass,
  placeholderTextClass,
  selectItemClass,
  selectItemSizeVariants,
  selectLabelSizeVariants,
  selectListClass,
  selectPopupClass,
  selectPositionerClass,
  selectTriggerClass,
  selectTriggerSizeVariants,
  selectValueClass,
  selectValueSizeVariants,
  selectWrapperClass,
  triggerIndicatorsContainerClass,
} from "./styles.css";
import type { CustomSelectProps } from "./types";
import { VirtualizedSelect } from "./Virtualized";

export type { CustomSelectProps, Option, SelectSize } from "./types";

const StandardSelect = forwardRef<HTMLButtonElement, CustomSelectProps>(
  (
    {
      label,
      error,
      info,
      isDisabled,
      isLoading,
      isMulti,
      isClearable,
      isSearchable,
      options = [],
      containerStyles,
      className,
      placeholder = "Selecione",
      emptyMessage = "Nada encontrado",
      value: valueProp,
      onChange,
      onValueChange,
      id,
      autoComplete,
      required,
      size = "large",
    },
    ref,
  ) => {
    const currentValue =
      valueProp !== undefined ? valueProp : isMulti ? [] : null;

    const hasValue = isMulti
      ? Array.isArray(currentValue) && currentValue.length > 0
      : currentValue !== null && currentValue !== undefined;

    const handleValueChange = (newValue: any) => {
      onChange?.(newValue);
      onValueChange?.(newValue);
    };

    const handleClear = (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      const empty = isMulti ? [] : null;
      handleValueChange(empty);
    };

    const fieldMessage = (
      <FieldMessage
        error={error}
        info={info}
        isDisabled={isDisabled || isLoading}
      />
    );

    if (isSearchable) {
      return (
        <Field.Root
          className={clsx(selectWrapperClass, className)}
          style={containerStyles}
          invalid={!!error}
          data-slot="select-root"
        >
          {label && (
            <Field.Label
              className={selectLabelSizeVariants[size]}
              data-slot="select-label"
            >
              {label}
            </Field.Label>
          )}
          <Combobox.Root
            items={options.map((o) => o.value)}
            value={currentValue}
            onValueChange={handleValueChange}
            multiple={isMulti}
            disabled={isDisabled || isLoading}
            itemToStringLabel={(v: any) =>
              options.find((opt) => opt.value === v)?.label ?? String(v ?? "")
            }
            id={id}
          >
            <div style={{ position: "relative", width: "100%" }}>
              <Combobox.Input
                className={clsx(
                  comboboxInputClass,
                  comboboxInputSizeVariants[size],
                )}
                placeholder={placeholder}
                autoComplete="off"
                style={{ paddingRight: "40px" }}
              />
              <div
                className={triggerIndicatorsContainerClass}
                data-size={size}
                style={{
                  position: "absolute",
                  right: 0,
                  top: 0,
                  height: "100%",
                  pointerEvents: "none",
                }}
              >
                {isClearable && hasValue && (
                  <button
                    type="button"
                    className={clearButtonClass}
                    style={{ pointerEvents: "all" }}
                    onClick={handleClear}
                  >
                    <IconClose />
                  </button>
                )}
                {isLoading ? (
                  <Spinner color={vars.colors.neutral.light} size={16} />
                ) : (
                  <IconChevronDown className="custom-dropdown-icon" />
                )}
              </div>
            </div>
            <Combobox.Portal>
              <Combobox.Positioner
                className={selectPositionerClass}
                sideOffset={4}
              >
                <Combobox.Popup className={selectPopupClass}>
                  <Combobox.Empty className={noOptionsMessageClass}>
                    {emptyMessage}
                  </Combobox.Empty>
                  <Combobox.List className={selectListClass}>
                    {(itemValue: any) => {
                      const opt = options.find((o) => o.value === itemValue);
                      if (!opt) return null;
                      return (
                        <Combobox.Item
                          key={opt.value}
                          value={opt.value}
                          disabled={opt.isDisabled}
                          className={clsx(
                            selectItemClass,
                            selectItemSizeVariants[size],
                          )}
                        >
                          {opt.label}
                          <Combobox.ItemIndicator
                            className={itemIndicatorClass}
                            keepMounted
                          >
                            ✓
                          </Combobox.ItemIndicator>
                        </Combobox.Item>
                      );
                    }}
                  </Combobox.List>
                </Combobox.Popup>
              </Combobox.Positioner>
            </Combobox.Portal>
          </Combobox.Root>
          {fieldMessage}
        </Field.Root>
      );
    }

    return (
      <Field.Root
        className={clsx(selectWrapperClass, className)}
        style={containerStyles}
        invalid={!!error}
        aria-disabled={isDisabled || isLoading}
        data-slot="select-root"
      >
        {label && (
          <Field.Label
            className={selectLabelSizeVariants[size]}
            data-slot="select-label"
          >
            {label}
          </Field.Label>
        )}
        <BaseSelect.Root
          value={currentValue}
          onValueChange={handleValueChange}
          multiple={isMulti}
          disabled={isDisabled || isLoading}
          id={id}
          autoComplete={autoComplete}
          required={required}
        >
          <BaseSelect.Trigger
            ref={ref}
            className={clsx(
              selectTriggerClass,
              selectTriggerSizeVariants[size],
            )}
            data-slot="select-trigger"
          >
            <BaseSelect.Value
              className={clsx(selectValueClass, selectValueSizeVariants[size])}
            >
              {(value: any) => {
                const isEmpty =
                  value === null ||
                  value === undefined ||
                  (isMulti && Array.isArray(value) && value.length === 0);

                if (isEmpty) {
                  return (
                    <span className={placeholderTextClass}>{placeholder}</span>
                  );
                }

                if (isMulti && Array.isArray(value)) {
                  const selectedOpts = options.filter((opt) =>
                    value.includes(opt.value),
                  );
                  return selectedOpts.map((opt) => (
                    <span key={opt.value} className={multiValueBadgeClass}>
                      {opt.label}
                    </span>
                  ));
                }

                return (
                  options.find((opt) => opt.value === value)?.label ??
                  String(value)
                );
              }}
            </BaseSelect.Value>
            <div className={triggerIndicatorsContainerClass} data-size={size}>
              {isClearable && hasValue && (
                <button
                  type="button"
                  className={clearButtonClass}
                  onClick={handleClear}
                >
                  <IconClose />
                </button>
              )}
              {isLoading ? (
                <Spinner color={vars.colors.neutral.light} size={16} />
              ) : (
                <IconChevronDown className="custom-dropdown-icon" />
              )}
            </div>
          </BaseSelect.Trigger>
          <BaseSelect.Portal>
            <BaseSelect.Positioner
              className={selectPositionerClass}
              sideOffset={4}
              alignItemWithTrigger={false}
            >
              <BaseSelect.Popup
                className={selectPopupClass}
                data-slot="select-popup"
              >
                <BaseSelect.List
                  className={selectListClass}
                  data-slot="select-list"
                >
                  {options.length > 0 ? (
                    options.map((opt) => (
                      <BaseSelect.Item
                        key={opt.value}
                        value={opt.value}
                        disabled={opt.isDisabled}
                        className={clsx(
                          selectItemClass,
                          selectItemSizeVariants[size],
                        )}
                      >
                        <BaseSelect.ItemText>{opt.label}</BaseSelect.ItemText>
                        <BaseSelect.ItemIndicator
                          className={itemIndicatorClass}
                          keepMounted
                        >
                          ✓
                        </BaseSelect.ItemIndicator>
                      </BaseSelect.Item>
                    ))
                  ) : (
                    <div className={noOptionsMessageClass}>{emptyMessage}</div>
                  )}
                </BaseSelect.List>
              </BaseSelect.Popup>
            </BaseSelect.Positioner>
          </BaseSelect.Portal>
        </BaseSelect.Root>
        {fieldMessage}
      </Field.Root>
    );
  },
);

export const Select = forwardRef<HTMLButtonElement, CustomSelectProps>(
  ({ virtualized, ...props }, ref) =>
    virtualized ? (
      <VirtualizedSelect {...props} ref={ref} />
    ) : (
      <StandardSelect {...props} ref={ref} />
    ),
);

StandardSelect.displayName = "StandardSelect";
Select.displayName = "Select";
